"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { AlertTriangle, Newspaper, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { createLumiApiClient, type BookSummary } from "@/lib/lumi-api";
import { deleteOfflineBook } from "@/lib/offline/book-cache";
import BookCover from "./BookCover";
import BookCover3D from "./BookCover3D";
import BookCoverLoading from "./BookCoverLoading";
import CoverageBadge from "./CoverageBadge";
import SaveOfflineButton from "./SaveOfflineButton";
import { bookKind, type CoverStyle } from "./libraryView";
import {
  bookProgress,
  coverImageUrl,
  difficultyLabel,
  languageLabel,
} from "./bookMeta";

interface RustBookCardProps {
  book: BookSummary;
  /** "solid" wraps the artwork in the 3D book frame. Defaults to flat. */
  coverStyle?: CoverStyle;
}

// Matches the import form + public catalog difficulty scale.
const LEVELS: Array<[string, string]> = [
  ["1", "Newbie"],
  ["3", "Beginner"],
  ["5", "Intermediate"],
  ["7", "Upper Intermediate"],
  ["9", "Advanced"],
  ["12", "Fluent"],
];

// 44px minimum touch target (WCAG 2.5.5) — the shadcn `size="icon"` default is
// 40px, so the card sizes its icon buttons explicitly. `border-app-muted`
// replaces the default `border-input`, which measures 1.23:1 against the Paper
// surface and misses the 3:1 owed to a control boundary (WCAG 1.4.11).
const ICON_BUTTON = "h-11 w-11 shrink-0 border-app-muted";

export default function RustBookCard({
  book,
  coverStyle = "flat",
}: RustBookCardProps) {
  const router = useRouter();
  const api = useMemo(() => createLumiApiClient(), []);
  const [name, setName] = useState(book.title);
  const [level, setLevel] = useState(book.language_level ?? "5");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const levelChanged = level !== (book.language_level ?? "5");

  const cover = coverImageUrl(book);
  const levelLabel = difficultyLabel(book.language_level);
  const target = languageLabel(book.target_language);
  const progress = bookProgress(book);
  const isPending = progress.state === "importing" || progress.state === "translating";
  const isError = progress.state === "error";
  const isArticle = bookKind(book) === "article";
  const itemNoun = isArticle ? "article" : "book";

  async function save() {
    const next = name.trim();
    const titleChanged = Boolean(next) && next !== book.title;
    if (!titleChanged && !levelChanged) {
      setEditOpen(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Sending language_level re-translates the book at that level (backend
      // drops + regenerates the target translation, then re-runs TTS).
      await api.updateBook(book.id, {
        ...(titleChanged ? { title: next } : {}),
        ...(levelChanged ? { language_level: level } : {}),
      });
      setEditOpen(false);
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save changes");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteBook(book.id);
      // Invalidate the book's offline copy alongside the server delete.
      await deleteOfflineBook(book.id).catch(() => {});
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete book");
      setDeleting(false);
    }
  }

  // The artwork is identical in both presentations; only the frame differs.
  const artwork = isPending ? (
    <BookCoverLoading
      indeterminate={progress.indeterminate}
      label={progress.state === "importing" ? "Preparing" : "Translating"}
      percent={progress.percent}
      src={cover}
      title={book.title}
    />
  ) : (
    <BookCover
      className={[
        isError ? "opacity-60 grayscale" : "",
        // The flat presentation zooms the art on hover; the solid one already
        // moves the whole book, so its artwork stays put.
        coverStyle === "solid"
          ? ""
          : "transition-transform duration-300 group-hover:scale-[1.03]",
      ]
        .filter(Boolean)
        .join(" ")}
      src={cover}
      title={book.title}
    />
  );

  const coverBlock =
    coverStyle === "solid" ? (
      <BookCover3D>{artwork}</BookCover3D>
    ) : (
      <div className="h-full w-full overflow-hidden">{artwork}</div>
    );

  // The solid frame needs the overflow to stay visible so the spine and drop
  // shadow aren't clipped; the flat one keeps the original hard crop.
  const coverFrame = `block h-64 w-full ${
    coverStyle === "solid" ? "bg-app-tinted/50" : "overflow-hidden bg-app-tinted"
  }`;

  return (
    <div className="group flex flex-col overflow-hidden rounded-md bg-app-surface shadow-md transition-shadow duration-300 hover:shadow-lg">
      {isPending ? (
        // Not readable yet — no link, so a half-imported book can't be opened.
        <div className={coverFrame}>{coverBlock}</div>
      ) : (
        // The Read button below is the accessible route into the book, so the
        // cover is skipped in the tab order rather than duplicating that stop.
        <Link className={coverFrame} href={`/read/${book.id}`} tabIndex={-1}>
          {coverBlock}
        </Link>
      )}

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          {/* Title leads the card: two full lines at 18px before it truncates,
              so "The Fables of Aesop" reads as a title rather than "The Fables
              of …". `break-words` keeps one long word from widening the grid. */}
          <h2
            className="line-clamp-2 break-words text-lg font-semibold leading-snug text-app-text"
            title={book.title}
          >
            {book.title}
          </h2>

          {/* Metadata was `text-app-muted`, which is 3.19:1 on the Paper
              surface — under the 4.5:1 floor. `text-app-body` is 13.97:1
              (Paper) / 13.46:1 (Ink). */}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-app-body">
            {isArticle && (
              <span className="inline-flex items-center gap-1 rounded-full border border-app-muted px-1.5 py-0.5 font-medium">
                <Newspaper aria-hidden="true" className="h-3 w-3" />
                Article
              </span>
            )}
            {levelLabel && <span>{levelLabel}</span>}
            {levelLabel && target && (
              <span aria-hidden="true" className="text-app-muted">
                ·
              </span>
            )}
            {target && <span>{target}</span>}
            {!isPending && !isError && <CoverageBadge bookId={book.id} />}
          </div>
        </div>

        {/* One action row, pinned to the bottom of the card: the two icon
            actions sit beside Read instead of floating up by the title. */}
        <div className="mt-auto flex items-center gap-2">
          {isPending ? (
            <span className="flex min-h-[44px] flex-1 items-center gap-2 text-xs font-medium text-app-body">
              <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-app-accent" />
              {progress.indeterminate
                ? "Preparing…"
                : `Translating · ${progress.percent}%`}
            </span>
          ) : isError ? (
            <span className="flex min-h-[44px] flex-1 items-center gap-2 text-xs font-medium text-destructive">
              <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
              Translation failed
            </span>
          ) : (
            <>
              <SaveOfflineButton bookId={book.id} className={ICON_BUTTON} />
              <Button
                aria-label={`Edit ${book.title}`}
                className={ICON_BUTTON}
                onClick={() => setEditOpen(true)}
                size="icon"
                title={`Edit ${book.title}`}
                type="button"
                variant="outline"
              >
                <Pencil aria-hidden="true" className="h-4 w-4" />
              </Button>
              <Link className="min-w-0 flex-1" href={`/read/${book.id}`}>
                <Button className="h-11 w-full bg-app-accent px-3 text-app-on-accent hover:opacity-90">
                  Read
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Edit dialog, opened by the icon button above. Kept outside the action
          row so that row stays a plain flex line whatever the dialog is doing. */}
      <Dialog onOpenChange={setEditOpen} open={editOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit {itemNoun}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm" htmlFor={`name-${book.id}`}>
                Name
              </label>
              <Input
                className="col-span-3"
                id={`name-${book.id}`}
                onChange={(event) => setName(event.target.value)}
                value={name}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm" htmlFor={`level-${book.id}`}>
                Level
              </label>
              <select
                className="col-span-3 h-10 rounded-md border border-input bg-background px-2 text-sm"
                id={`level-${book.id}`}
                onChange={(event) => setLevel(event.target.value)}
                value={level}
              >
                {LEVELS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {levelChanged && (
              <p className="text-xs text-app-body">
                Changing the level re-translates the {itemNoun} at that
                difficulty — it&rsquo;ll show &ldquo;Preparing&rdquo; briefly
                while it regenerates.
              </p>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter className="flex flex-col items-stretch gap-2 sm:flex-col">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="w-full" type="button" variant="destructive">
                  Delete {itemNoun}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes &ldquo;{book.title}&rdquo; and its
                    reading progress. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleting}
                    onClick={remove}
                  >
                    {deleting ? "Deleting…" : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button disabled={saving} onClick={save} type="button">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
